<template>
  <MarketCard>
    <div :class="classes.cardHeader">
      <CardHeader :class="classes.datesport" :eventId="eventId" />
    </div>

    <div :class="classes.cardContent">
      <EventNames
        :class="classes.leftContent"
        :eventId="eventId"
        :marketId="marketId"
      />

      <div :class="classes.promoWrapper">
        <PromoLogo
          :class="classes.promoLogo"
          :promoMarketName="marketData.promoMarketName"
          :marketName="marketData.marketName"
        />
        <div :class="classes.promoText">
          <PromoFittedText
            :promoText="promoText"
            alignment="center"
          ></PromoFittedText>
        </div>

        <Button
          :class="[classes.oddsButton]"
          :marketId="marketId"
          :option="option"
        />
      </div>
    </div>
  </MarketCard>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { defineComponent, computed, ComputedRef } from "vue";
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import Button from "../Button.vue";
import EventNames from "../../../EventNamesBadgesStacked.vue";
import MarketCard from "../../../styled/MarketCard.vue";
import CardHeader from "../../../CardHeaderLeaderboard.vue";
import PromoLogo from "../Logo.vue";
import PromoFittedText from "../../../PromoFittedText.vue";

import {
  composeMarket,
  composeEvent
} from "@fresh8/ad-data-composition/src/betData";
import { Brand } from "@fresh8/types/src";
import composeDate from "@fresh8/ad-data-composition/src/date";
import { injectCarouselContext } from "../../../../lib/provideInjectors";
import { Market, SportEvent } from "@fresh8/types/src/betData";
import { injectPromoOption } from "../../../../hooks/usePromoOption";

const useStyles = createUseStyles({
  promoCardWrapper: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    height: "100%",
    border: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    borderRadius: props => `${props.borderRadius}px`,
    position: "relative",
    boxSizing: "border-box"
  },

  cardHeader: {
    position: "absolute",
    width: "40%"
  },

  datesport: {
    "& span": {
      fontSize: "10px !important"
    }
  },
  marketName: {
    margin: "0 8px",
    display: "flex",
    justifyContent: "space-evenly"
  },

  cardContent: {
    display: "flex",
    flex: 1
  },

  leftContent: {
    margin: "17px 0 6px",
    paddingTop: "6px",
    paddingBottom: "6px"
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1
  },
  promoWrapper: {
    flex: "1 0 50%",
    padding: "6px",
    display: "flex",
    overflow: "hidden",
    boxSizing: "border-box",
    minHeight: "0px",
    flexDirection: "column",
    borderLeft: props => `1px solid ${props.marketBorderColor}`
  },
  promoText: {
    flex: "1 1 0px",
    display: "flex",
    padding: "3px",
    overflow: "hidden",
    boxSizing: "border-box",
    minHeight: "0px",
    flexDirection: "column"
  },

  promoLogo: {
    flex: "1",
    width: "100%",
    maxHeight: "20px",
    alignSelf: "center",
    "&.promo-market-name": {
      fontSize: "12px !important"
    }
  },
  oddsButton: {
    height: "25px",
    "& .odds-text": {
      fontSize: "12px"
    }
  },

  resize: {
    pointerEvents: "none",
    zIndex: -99
  }
});

export default defineComponent({
  name: "SmallResponsive",
  components: {
    CardHeader,
    EventNames,
    PromoFittedText,
    Button,
    PromoLogo,
    MarketCard
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const { adTemplateBranding, marketBranding, backgroundImageSets } =
      injectBrand() || ({} as BrandContext);

    const backgroundImage = computed(() => {
      return !!Object.keys(backgroundImageSets?.value || {}).length;
    });

    const carouselContext = injectCarouselContext();

    const relativeStyles = computed(() => ({
      ...adTemplateBranding.value,
      backgroundImage: backgroundImage.value,
      isCarousel: carouselContext.value.isCarousel
    }));

    const classesRef = useStyles(relativeStyles);

    const event = composeEvent(props.eventId) as ComputedRef<SportEvent>;
    const fixtureStartDate = composeDate(event.value?.id);

    const marketData = composeMarket(props.marketId) as ComputedRef<Market>;

    const option = injectPromoOption();

    return {
      marketData,
      marketBranding,
      classes: classesRef,
      option,
      promoText: computed(() => option.value?.promoText || ""),
      event,
      fixtureStartDate,
      marketNameStyle: {
        get(brand: Brand) {
          return brand.adTemplate.market.marketNameFont;
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.marketNameFont = value;
        }
      },
      fixtureTimeFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureTimeFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureTimeFont = value;
        }
      },
      fixtureSportFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureSportFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureSportFont = value;
        }
      },
      fixtureNameStyle: {
        get: (brand: Brand) => brand.adTemplate.market.teamNameFont,
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});
</script>
